import {
  ShimmeredDetailsList,
  DetailsListLayoutMode,
  IColumn,
  IconButton,
  Label,
  MessageBar,
  MessageBarType,
  Modal,
  PrimaryButton,
  SelectionMode,
  Stack,
  StackItem,
  mergeStyleSets,
} from "@fluentui/react";
import { useBoolean } from "@fluentui/react-hooks";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ProjectService from "../../../common/api-services/ProjectServices";
import { ITermsLog } from "../../../common/models/ITermsLog";
import styles from "./termsLogs.module.scss";

const TermsLogs: React.FC = () => {
  const service = useMemo(() => new ProjectService(), []);
  const { projectId = "" } = useParams();
  const [items, setItems] = useState<ITermsLog[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [selectedTac, setSelectedTac] = useState<ITermsLog | null>(null);
  const [isTacModalOpen, { setTrue: showTacModal, setFalse: hideTacModal }] =
    useBoolean(false);
  const navigate = useNavigate();

  const contentStyles = mergeStyleSets({
    container: {
      display: "flex",
      flexFlow: "column nowrap",
      alignItems: "stretch",
      border: "1px solid #ccc",
      borderRadius: "8px",
      maxWidth: "900px",
    },
  });

  useEffect(() => {
    service
      .GetTermsLogsByProject(projectId)
      .then((data) => {
        setItems(data ?? []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.message ?? "Failed to load terms logs.");
        setLoading(false);
      });
  }, [service, projectId]);

  const _onFormatDate = (dateInput?: string): string => {
    if (!dateInput) return "";
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${dd}.${mm}.${yyyy} ${hh}:${min}`;
  };

  const columns: IColumn[] = [
    {
      key: "email",
      name: "E-mail",
      fieldName: "email",
      minWidth: 150,
      maxWidth: 250,
      isRowHeader: true,
      onRender: (item: ITermsLog) => <span>{item.email ?? ""}</span>,
    },
    {
      key: "name",
      name: "Name",
      fieldName: "name",
      minWidth: 120,
      maxWidth: 200,
      isRowHeader: true,
      onRender: (item: ITermsLog) => <span>{item.name ?? ""}</span>,
    },
    {
      key: "companyName",
      name: "Company",
      fieldName: "companyName",
      minWidth: 120,
      maxWidth: 200,
      isRowHeader: true,
      onRender: (item: ITermsLog) => <span>{item.companyName ?? ""}</span>,
    },
    {
      key: "acceptanceDate",
      name: "Acceptance time",
      fieldName: "acceptanceDate",
      minWidth: 130,
      maxWidth: 180,
      isRowHeader: true,
      onRender: (item: ITermsLog) => (
        <span>{_onFormatDate(item.acceptanceDate)}</span>
      ),
    },
    {
      key: "tac",
      name: "Terms",
      minWidth: 80,
      maxWidth: 100,
      isRowHeader: true,
      onRender: (item: ITermsLog) => (
        <IconButton
          iconProps={{ iconName: "View" }}
          title="View terms"
          ariaLabel="View terms"
          disabled={!item.tacContent}
          onClick={() => {
            setSelectedTac(item);
            showTacModal();
          }}
        />
      ),
    },
  ];

  return (
    <>
      <Stack verticalAlign="start">
        <Stack horizontalAlign="space-between" horizontal>
          <StackItem align="start">
            <Label className={styles.PageHeader}>Terms Logs</Label>
          </StackItem>
          <StackItem align="end">
            <PrimaryButton
              text="Back"
              className={styles.buttonNewProject}
              onClick={() => navigate(`/project/${projectId}`)}
            />
          </StackItem>
        </Stack>
        <StackItem>
          <hr className={styles.horizontalLine}></hr>
        </StackItem>

        {error !== "" && (
          <MessageBar
            messageBarType={MessageBarType.error}
            isMultiline={false}
            dismissButtonAriaLabel="Close"
            onDismiss={() => setError("")}
          >
            {error}
          </MessageBar>
        )}

        <ShimmeredDetailsList
          items={items}
          enableShimmer={loading}
          columns={columns}
          layoutMode={DetailsListLayoutMode.justified}
          selectionMode={SelectionMode.none}
          isHeaderVisible={true}
        />
      </Stack>

      <Modal
        isOpen={isTacModalOpen}
        onDismiss={hideTacModal}
        isBlocking={false}
        containerClassName={contentStyles.container}
      >
        <div className={styles.tacModalHeader}>
          <span>Terms</span>
          <IconButton
            iconProps={{ iconName: "Cancel" }}
            ariaLabel="Close"
            onClick={hideTacModal}
          />
        </div>
        {selectedTac && !selectedTac.tacVersionAvailable && (
          <div style={{ padding: "0 20px" }}>
            <MessageBar messageBarType={MessageBarType.warning}>
              The exact version of the terms accepted by this user could not be
              found. Displaying the current version instead.
            </MessageBar>
          </div>
        )}
        <div
          className={styles.tacModalContainer}
          dangerouslySetInnerHTML={{ __html: selectedTac?.tacContent ?? "" }}
        />
      </Modal>
    </>
  );
};

export default TermsLogs;
